// Holds the last domain session so FE-only view transitions and chosenSourceId survive refetches.

import { ApolloClient } from '@apollo/client';
import { ScenarioInput } from 'src/graphql/_generated';
import type {
  AiAssistantAbandonMutation,
  AiAssistantAbandonMutationVariables,
  AiAssistantAcceptMutation,
  AiAssistantAcceptMutationVariables,
  AiAssistantActiveSessionQuery,
  AiAssistantChooseFrequencyMutation,
  AiAssistantChooseFrequencyMutationVariables,
  AiAssistantRefineMutation,
  AiAssistantRefineMutationVariables,
  AiAssistantSelectCycleMutation,
  AiAssistantSelectCycleMutationVariables,
  AiAssistantSessionQuery,
  AiAssistantSessionQueryVariables,
  AiAssistantStartSessionMutation,
  AiAssistantStartSessionMutationVariables,
  AiAssistantSubmitImpactsMutation,
  AiAssistantSubmitImpactsMutationVariables,
} from 'src/graphql/_generated';
import { RiskConsequences, RiskEvents, RiskSources } from 'src/api/types';
import {
  AI_ASSISTANT_ACTIVE_SESSION,
  AI_ASSISTANT_SESSION,
} from 'src/graphql/schemas/queries/aiAssistant';
import {
  AI_ASSISTANT_ABANDON,
  AI_ASSISTANT_ACCEPT,
  AI_ASSISTANT_CHOOSE_FREQUENCY,
  AI_ASSISTANT_REFINE,
  AI_ASSISTANT_SELECT_CYCLE,
  AI_ASSISTANT_START_SESSION,
  AI_ASSISTANT_SUBMIT_IMPACTS,
} from 'src/graphql/schemas/mutations/aiAssistant';
import { subscribeToAiSession } from 'src/realtime/aiSessionSocket';
import { aiLog } from 'src/utils/logger';
import { RiskIntelEvent, RiskIntelSession } from '../../types';
import { enumNameToInt } from '../../util/enums';
import { annualizedToTimesYears } from '../../util/frequency';
import { toScenarioFormula } from '../../util/formula';
import { RiskIntelService, StartSessionInput } from '../RiskIntelService';
import { graphqlSessionToDomain } from './adapter';

type WireSession = AiAssistantStartSessionMutation['aiAssistantStartSession'];

// Finished sessions must not be re-opened by a stray refetch.
const TERMINAL_STATUSES = new Set(['completed', 'abandoned', 'errored']);

const summarise = (s: RiskIntelSession) => ({
  id: s.id,
  status: s.status,
  selectedCycleId: s.selectedCycleId,
  impactsAccepted: s.impactsAccepted,
  cycles: s.cycles.map((c) => ({
    id: c.id,
    state: c.validation?.state,
    source: c.risk?.source,
    event: c.risk?.event,
    consequence: c.risk?.consequence,
  })),
  frequency: s.frequency && {
    suggestedValue: s.frequency.suggestedValue,
    chosenValue: s.frequency.chosenValue,
    chosenTimes: s.frequency.chosenTimes,
    chosenYears: s.frequency.chosenYears,
    chosenSourceId: s.frequency.chosenSourceId,
    sources: s.frequency.sources.length,
  },
  impacts: s.impacts?.map((i) => ({
    id: i.id,
    name: i.name,
    selected: i.selected,
    hasFormula: !!i.formula,
    lowerRaw: i.formula?.lowerRaw,
    upperRaw: i.formula?.upperRaw,
  })),
});

export class GraphqlRiskIntelService implements RiskIntelService {
  private current: RiskIntelSession | null = null;

  constructor(private client: ApolloClient<object>, private workspaceId?: string) {}

  /** Adapt a wire session → domain, preserving FE-only fields the wire can't carry. */
  private adopt(op: string, wire: WireSession): RiskIntelSession {
    aiLog.info(`← ${op} (wire)`, wire);
    const domain = graphqlSessionToDomain(wire);
    if (this.current?.frequency?.chosenSourceId && domain.frequency) {
      domain.frequency.chosenSourceId = this.current.frequency.chosenSourceId; // exact card
    }
    if (this.current?.impactsAccepted) domain.impactsAccepted = true;
    // 'review' is FE-only; hold it across refetches (unless terminal) so a stray update can't eject the user.
    if (this.current?.status === 'review' && !TERMINAL_STATUSES.has(domain.status)) {
      domain.status = 'review';
    }
    this.current = domain;
    aiLog.info(`= ${op} (domain)`, summarise(domain));
    return domain;
  }

  private require(): RiskIntelSession {
    if (!this.current) throw new Error('No active AI session');
    return this.current;
  }

  async startSession(input: StartSessionInput): Promise<RiskIntelSession> {
    const variables = {
      userInput: input.userInput,
      assessmentId: input.assessmentId,
      workspaceId: input.workspaceId ?? this.workspaceId,
    };
    aiLog.info('→ startSession', variables);
    this.current = null; // new session — drop preserved FE-only state
    const { data } = await this.client.mutate<AiAssistantStartSessionMutation, AiAssistantStartSessionMutationVariables>(
      { mutation: AI_ASSISTANT_START_SESSION, fetchPolicy: 'no-cache', variables },
    );
    if (!data?.aiAssistantStartSession) throw new Error('startSession returned no session');
    return this.adopt('startSession', data.aiAssistantStartSession);
  }

  async refine(sessionId: string, userInput: string): Promise<RiskIntelSession> {
    aiLog.info('→ refine', { sessionId, userInput });
    const { data } = await this.client.mutate<AiAssistantRefineMutation, AiAssistantRefineMutationVariables>({
      mutation: AI_ASSISTANT_REFINE, fetchPolicy: 'no-cache',
      variables: { sessionId, userInput },
    });
    if (!data?.aiAssistantRefine) throw new Error('refine returned no session');
    return this.adopt('refine', data.aiAssistantRefine);
  }

  async selectCycle(sessionId: string, cycleId: string): Promise<RiskIntelSession> {
    aiLog.info('→ selectCycle', { sessionId, cycleId });
    const { data } = await this.client.mutate<AiAssistantSelectCycleMutation, AiAssistantSelectCycleMutationVariables>({
      mutation: AI_ASSISTANT_SELECT_CYCLE, fetchPolicy: 'no-cache',
      variables: { sessionId, cycleId },
    });
    if (!data?.aiAssistantSelectCycle) throw new Error('selectCycle returned no session');
    return this.adopt('selectCycle', data.aiAssistantSelectCycle);
  }

  async chooseFrequency(sessionId: string, chosenValue: number, chosenSourceId?: string): Promise<RiskIntelSession> {
    const { times, years } = annualizedToTimesYears(chosenValue);
    aiLog.info('→ chooseFrequency', { sessionId, chosenValue, chosenTimes: times, chosenYears: years, chosenSourceId });
    const { data } = await this.client.mutate<
      AiAssistantChooseFrequencyMutation,
      AiAssistantChooseFrequencyMutationVariables
    >({
      mutation: AI_ASSISTANT_CHOOSE_FREQUENCY, fetchPolicy: 'no-cache',
      variables: { sessionId, chosenTimes: times, chosenYears: years },
    });
    if (!data?.aiAssistantChooseFrequency) throw new Error('chooseFrequency returned no session');
    const domain = this.adopt('chooseFrequency', data.aiAssistantChooseFrequency);
    if (domain.frequency && chosenSourceId) domain.frequency.chosenSourceId = chosenSourceId; // exact card
    this.current = domain;
    return domain;
  }

  async submitImpacts(sessionId: string, selectedImpactIds: string[]): Promise<RiskIntelSession> {
    aiLog.info('→ submitImpacts', { sessionId, selectedImpactIds });
    const { data } = await this.client.mutate<
      AiAssistantSubmitImpactsMutation,
      AiAssistantSubmitImpactsMutationVariables
    >({
      mutation: AI_ASSISTANT_SUBMIT_IMPACTS, fetchPolicy: 'no-cache',
      variables: { sessionId, selectedImpactIds },
    });
    if (!data?.aiAssistantSubmitImpacts) throw new Error('submitImpacts returned no session');
    return this.adopt('submitImpacts', data.aiAssistantSubmitImpacts);
  }

  async abandon(sessionId: string): Promise<RiskIntelSession | null> {
    aiLog.info('→ abandon', { sessionId });
    const { data } = await this.client.mutate<AiAssistantAbandonMutation, AiAssistantAbandonMutationVariables>({
      mutation: AI_ASSISTANT_ABANDON, fetchPolicy: 'no-cache',
      variables: { sessionId },
    });
    return data?.aiAssistantAbandon ? this.adopt('abandon', data.aiAssistantAbandon) : null;
  }

  async accept(sessionId: string): Promise<RiskIntelSession> {
    aiLog.info('→ accept', { sessionId });
    const { data } = await this.client.mutate<AiAssistantAcceptMutation, AiAssistantAcceptMutationVariables>({
      mutation: AI_ASSISTANT_ACCEPT, fetchPolicy: 'no-cache',
      variables: { sessionId },
    });
    if (!data?.aiAssistantAccept) throw new Error('accept returned no session');
    return this.adopt('accept', data.aiAssistantAccept);
  }

  async getSession(sessionId: string): Promise<RiskIntelSession | null> {
    aiLog.info('→ getSession', { sessionId });
    const { data } = await this.client.query<AiAssistantSessionQuery, AiAssistantSessionQueryVariables>({
      query: AI_ASSISTANT_SESSION,
      variables: { sessionId },
      fetchPolicy: 'no-cache',
    });
    return data.aiAssistantSession ? this.adopt('getSession', data.aiAssistantSession) : null;
  }

  async getActiveSession(): Promise<RiskIntelSession | null> {
    aiLog.info('→ getActiveSession');
    const { data } = await this.client.query<AiAssistantActiveSessionQuery>({
      query: AI_ASSISTANT_ACTIVE_SESSION,
      fetchPolicy: 'no-cache',
    });
    if (!data.aiAssistantActiveSession) {
      aiLog.info('← getActiveSession: no active session');
      return null;
    }
    this.current = null; // possibly-different session — start FE-only state fresh
    return this.adopt('getActiveSession', data.aiAssistantActiveSession);
  }

  // FE-local view transitions

  async changeImpacts(): Promise<RiskIntelSession> {
    aiLog.info('· changeImpacts (local)');
    const c = this.require();
    this.current = {
      ...c,
      status: 'awaiting_impacts',
      impactsAccepted: false,
      impacts: c.impacts?.map((i) => ({ ...i, formula: undefined })),
    };
    return this.current;
  }

  async acceptImpacts(): Promise<RiskIntelSession> {
    aiLog.info('· acceptImpacts (local)');
    this.current = { ...this.require(), impactsAccepted: true };
    return this.current;
  }

  async proceedToReview(): Promise<RiskIntelSession> {
    aiLog.info('· proceedToReview (local)');
    this.current = { ...this.require(), status: 'review' };
    return this.current;
  }

  async backToIntel(): Promise<RiskIntelSession> {
    aiLog.info('· backToIntel (local)');
    this.current = { ...this.require(), status: 'awaiting_impacts' };
    return this.current;
  }

  subscribe(sessionId: string, onUpdate: (e: RiskIntelEvent) => void): () => void {
    aiLog.info('· subscribe', { sessionId });
    return subscribeToAiSession(sessionId, onUpdate);
  }

  toScenarioInput(session: RiskIntelSession): Partial<ScenarioInput> {
    const cycle =
      session.cycles.find((c) => c.id === session.selectedCycleId) ?? session.cycles[session.cycles.length - 1];
    const risk = cycle?.risk;
    if (!risk) return {};

    const sourceEnum = RiskSources as unknown as Record<string, string | number>;
    const eventEnum = RiskEvents as unknown as Record<string, string | number>;
    const consequenceEnum = RiskConsequences as unknown as Record<string, string | number>;

    // Carry raw chosenTimes/chosenYears straight through; avoids a lossy annualized round-trip.
    const times = session.frequency?.chosenTimes;
    const years = session.frequency?.chosenYears;

    // Join selected impacts into one lower/upper formula, each parenthesised so the `+` is well-formed.
    const selectedImpacts = (session.impacts ?? []).filter((impact) => impact.selected && impact.formula);
    const lowerFormula = selectedImpacts.length
      ? selectedImpacts.map((impact) => `( ${toScenarioFormula(impact.formula!.lowerRaw, [])} )`).join(' + ')
      : undefined;
    const upperFormula = selectedImpacts.length
      ? selectedImpacts.map((impact) => `( ${toScenarioFormula(impact.formula!.upperRaw, [])} )`).join(' + ')
      : undefined;
    const consequenceNote = selectedImpacts.length
      ? selectedImpacts.map((impact) => impact.formula!.note.basisOfEstimate).join('\n\n')
      : undefined;

    // Tripwire: the committed formula must be numeric `#<id>` + literals only. A leftover `#<Name>` or
    // `[label]` token (a BE-translation regression) would ship a scenario the evaluator can't compute.
    const hasBadToken = (f?: string) => !!f && /#(?!\d)|\[/.test(f);
    if (hasBadToken(lowerFormula) || hasBadToken(upperFormula)) {
      aiLog.warn('riskIntel: committed formula has a non-numeric token', { lowerFormula, upperFormula });
    }

    return {
      name: risk.name,
      source: enumNameToInt(sourceEnum, risk.source) ?? undefined,
      event: enumNameToInt(eventEnum, risk.event) ?? undefined,
      consequence: enumNameToInt(consequenceEnum, risk.consequence) ?? undefined,
      riskNarrative: risk.narrative,
      frequencyTimes: times,
      frequencyYears: years,
      frequencyNote: session.frequency?.note.basisOfEstimate,
      lowerFormula,
      upperFormula,
      consequenceNote,
      status: 'Draft',
    };
  }
}
